import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useMeasure } from 'react-use';
import {
  ArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from 'use-query-params';

import { useTabNavigation } from 'src/components/Insights/Catalog/NavigationTabs';
import { ModalDialogProps } from 'src/components/Insights/Dialogs/ModalDialog';
import authSelectors from 'src/redux/auth/auth-selectors';
import catalogSelectors from 'src/redux/catalog/catalog-selectors';
import {
  loadCatalog,
  selectEntry as _selectEntry,
} from 'src/redux/catalog/catalog-slice';
import { StoreState } from 'src/redux/store';
import { createProvider } from 'src/util/provider';

export const [CatalogPageProvider, useCatalogPageContext] = createProvider(
  () => {
    const dispatch = useDispatch();
    const catalog = useSelector(catalogSelectors.getCatalog);
    const conversations = useSelector(catalogSelectors.getConversations);
    const allCodes = useSelector(catalogSelectors.getAllCodes);
    const allEntries = useSelector(catalogSelectors.getAllEntries);
    const isLoading = useSelector(catalogSelectors.isLoading);
    const organizationIds = useSelector((state: StoreState) =>
      authSelectors
        .getOrganizations(state)
        .map((organization) => organization.id)
    );
    const participants = useSelector(catalogSelectors.getParticipants);
    const selectedConversation = useSelector(
      catalogSelectors.getSelectedConversation
    );
    const selectedEntry = useSelector(catalogSelectors.getSelectedEntry);
    const iAmAdminOrOwner = useSelector(catalogSelectors.iAmAdminOrOwner);
    const sensemakers = useSelector(catalogSelectors.getSensemakers);
    const [codebookRef, { width: codebookWidth }] = useMeasure();

    // ••• CODEBOOK

    const codebookHasCodes = allCodes.some((code) => code.parentage.length > 1);

    // ••• INIT

    const match = useRouteMatch<{ catalogId: string }>();
    const catalogId = +match.params.catalogId;

    useEffect(() => {
      dispatch(loadCatalog(catalogId));
    }, [catalogId, dispatch]);

    // ••• MODAL

    const [modals, setModals] = useState<ModalDialogProps[]>([]);

    const showModal = (
      component: ReactElement,
      props: Partial<ModalDialogProps & { withCodebook?: boolean }> = {}
    ) => {
      const { sx = {}, withCodebook, onClose } = props;
      const _props = {
        open: true,
        ...props,
        sx: {
          // extra 12px to account for the main external padding
          width: `calc(100% - ${withCodebook ? codebookWidth + 12 : 0}px)`,
          ...sx,
        },
        onClose: () => {
          hideModal();
          onClose?.();
        },
        children: component,
      };
      setModals((prev) => [...prev, _props]);
    };

    const hideModal = () => setModals((prev) => prev.slice(0, -1));

    // ••• NAVIGATION

    const [urlParams, setUrlParams] = useQueryParams({
      tab: NumberParam,
      entryId: NumberParam,
      page: NumberParam,
      filters: ArrayParam,
      filterJoin: StringParam,
    });
    const { activeTab, setActiveTab: _setActiveTab } = useTabNavigation(
      urlParams.tab
    );
    const showingOverview = activeTab === Tab.Overview;
    const showingConversations = activeTab === Tab.Conversations;
    const showingHighlights = activeTab === Tab.Highlights;

    const setActiveTab = (tab: number) => {
      _setActiveTab(tab);
      setUrlParams({ ...urlParams, tab }, 'push');
    };

    const selectEntry = (entryId?: number, page?: number) => {
      if (page === 0) {
        page = 1;
      }
      if (!page) {
        page = urlParams.page;
      }
      setUrlParams({ ...urlParams, entryId: entryId, page }, 'push');
      dispatch(_selectEntry(entryId));
    };

    useEffect(() => {
      if (
        catalog.organization_id !== -1 &&
        organizationIds.indexOf(catalog.organization_id) === -1
      ) {
        setActiveTab(-1); // -1 triggers NotFoundPage
      } else if (urlParams.entryId) {
        selectEntry(urlParams.entryId);
      }
    }, []);

    // ••• CONTEXT

    return {
      catalog,
      conversations,
      allCodes,
      allEntries,
      isLoading,
      organizationIds,
      participants,
      selectedConversation,
      selectedEntry,
      selectEntry,
      iAmAdminOrOwner,
      sensemakers,
      dispatch,
      t: useTranslation().t,
      codebookHasCodes,

      activeTab,
      setActiveTab,
      urlParams,
      setUrlParams,
      showingOverview,
      showingConversations,
      showingHighlights,
      hasCodebook: showingConversations || showingHighlights,
      codebookRef,

      modals,
      showModal,
      hideModal,
    };
  }
);

// DEFINITIONS

export const Tab = {
  Highlights: 0,
  Conversations: 1,
  Sensemakers: 2,
  Overview: 3,
};
